import React, { useMemo, useState } from 'react';
import { Button, Card, Col, Empty, Row, Select, Space, Spin, Tag, Typography, message } from 'antd';
import { PlusOutlined, UploadOutlined } from '@ant-design/icons';
import type { Application, Route } from '../../types';
import {
  useGetDocumentsByApplicationQuery,
} from '../../store/api/documentsApi';
import { useGetRoutesByApplicationQuery, useGetRouteStopsQuery } from '../../store/api/routesApi';
import UploadDocumentModal from '../documents/UploadDocumentModal';
import DocumentTable from '../documents/DocumentTable';
import ApplicationDocumentFilters from '../documents/ApplicationDocumentFilters';
import DocumentPreviewModal from '../documents/DocumentPreviewModal';
import { useDocumentActions } from '../documents/useDocumentActions';
import { useApplicationRouteDocuments } from '../../hooks/useApplicationRouteDocuments';
import {
  type ApplicationDocumentFilterDraft,
  collectStopFilterOptions,
  filterApplicationDocumentRows,
  mergeApplicationDocuments,
} from '../../utils/applicationDocumentRegistry';
import type { DocumentUploadScope } from '../../utils/documentUploadScope';
import ClosingPackageBlock from './ClosingPackageBlock';
import {
  isBusinessDocumentDefinitionActive,
  listBusinessDocumentDefinitions,
  resolveApplicationBasis,
  type BusinessDocumentDefinition,
} from '../../utils/businessDocumentDefinitions';

const { Text } = Typography;

type UploadTargetType = 'application' | 'route' | 'stop';
type UploadPreset = {
  documentType: string;
  stage: 'loading' | 'unloading' | 'other';
  fileType: 'file' | 'photo';
};

export interface ApplicationDocumentsTabProps {
  application: Application;
}

const EMPTY_FILTERS: ApplicationDocumentFilterDraft = { source: 'all' };

const ApplicationDocumentsTab: React.FC<ApplicationDocumentsTabProps> = ({ application }) => {
  const [filters, setFilters] = useState<ApplicationDocumentFilterDraft>(EMPTY_FILTERS);
  const [uploadModalVisible, setUploadModalVisible] = useState(false);
  const [uploadTargetType, setUploadTargetType] = useState<UploadTargetType>('application');
  const [uploadRouteId, setUploadRouteId] = useState<number | undefined>();
  const [uploadStopId, setUploadStopId] = useState<number | undefined>();
  const [uploadPreset, setUploadPreset] = useState<UploadPreset | null>(null);

  const {
    data: applicationDocuments = [],
    isLoading: applicationDocsLoading,
    isFetching: applicationDocsFetching,
    refetch: refetchApplicationDocuments,
  } = useGetDocumentsByApplicationQuery(application.id);

  const {
    data: routes = [],
    isLoading: routesLoading,
  } = useGetRoutesByApplicationQuery(application.id);

  const routeIds = useMemo(() => routes.map((route) => route.id), [routes]);
  const {
    routeDocumentsByRouteId,
    isLoading: routeDocsLoading,
    refetchRouteDocuments,
  } = useApplicationRouteDocuments(routeIds);

  const { data: uploadRouteStops = [] } = useGetRouteStopsQuery(uploadRouteId as number, {
    skip: uploadTargetType !== 'stop' || uploadRouteId == null,
  });

  const refreshDocuments = () => {
    void refetchApplicationDocuments();
    refetchRouteDocuments();
  };

  const {
    previewVisible,
    previewImage,
    closePreview,
    handlePreview,
    handleDownload,
    handleDelete,
  } = useDocumentActions(refreshDocuments);

  const allRows = useMemo(
    () => mergeApplicationDocuments(application.id, applicationDocuments, routeDocumentsByRouteId, routes),
    [application.id, applicationDocuments, routeDocumentsByRouteId, routes]
  );

  const filteredRows = useMemo(
    () => filterApplicationDocumentRows(allRows, filters),
    [allRows, filters]
  );

  const stopOptions = useMemo(() => collectStopFilterOptions(allRows), [allRows]);

  const selectedUploadRoute: Route | undefined = useMemo(
    () => routes.find((route) => route.id === uploadRouteId),
    [routes, uploadRouteId]
  );

  const uploadScope: DocumentUploadScope | undefined = useMemo(() => {
    if (uploadTargetType !== 'stop' || uploadRouteId == null || uploadStopId == null) return undefined;
    const stop = uploadRouteStops.find((item: { id?: number }) => Number(item.id) === uploadStopId);
    return {
      applicationId: application.id,
      routeStopId: uploadStopId,
      groupId: stop?.groupId != null ? Number(stop.groupId) : undefined,
    };
  }, [uploadTargetType, uploadRouteId, uploadStopId, uploadRouteStops, application.id]);

  const loading = applicationDocsLoading || routesLoading || routeDocsLoading;
  const fetching = applicationDocsFetching;

  const handleUploadSuccess = () => {
    message.success('Документ успешно загружен');
    setUploadPreset(null);
    refreshDocuments();
  };

  const openUploadModal = () => {
    setUploadPreset(null);
    if (uploadTargetType === 'route' && !uploadRouteId) {
      message.warning('Выберите маршрут для загрузки');
      return;
    }
    if (uploadTargetType === 'stop' && (!uploadRouteId || !uploadStopId)) {
      message.warning('Выберите маршрут и стоп для загрузки');
      return;
    }
    setUploadModalVisible(true);
  };

  const clientDocumentPlan = useMemo(() => {
    return listBusinessDocumentDefinitions({
      party: 'CLIENT',
      basis: resolveApplicationBasis(application),
    })
      .filter((definition) => !['closing', 'settlement'].includes(definition.processStage))
      .map((definition) => {
      const uploaded = allRows.some((row) => row.documentType?.toUpperCase() === definition.legacyDocumentType.toUpperCase());
      const active = isBusinessDocumentDefinitionActive(definition, { routes });
      return { definition, uploaded, active };
    });
  }, [allRows, application, routes]);

  const openPlanUpload = (definition: BusinessDocumentDefinition) => {
    setUploadTargetType('application');
    setUploadRouteId(undefined);
    setUploadStopId(undefined);
    setUploadPreset({
      documentType: definition.legacyDocumentType,
      stage: definition.stage,
      fileType: definition.fileType,
    });
    setUploadModalVisible(true);
  };

  const planStatus = (row: { uploaded: boolean; active: boolean }) => {
    if (row.uploaded) return { label: 'Загружен', color: 'green' };
    if (!row.active) return { label: 'Недоступен', color: 'default' };
    return { label: 'Доступен', color: 'blue' };
  };

  return (
    <div>
      <Row justify="space-between" align="middle" style={{ marginBottom: 16 }} gutter={[12, 12]}>
        <Col flex="auto">
          <Space wrap align="end">
            <div>
              <Text type="secondary" style={{ display: 'block', marginBottom: 4 }}>
                Привязка загрузки
              </Text>
              <Select
                style={{ width: 140 }}
                value={uploadTargetType}
                onChange={(value: UploadTargetType) => {
                  setUploadTargetType(value);
                  setUploadRouteId(undefined);
                  setUploadStopId(undefined);
                }}
                options={[
                  { value: 'application', label: 'Заявка' },
                  { value: 'route', label: 'Маршрут' },
                  { value: 'stop', label: 'Стоп' },
                ]}
              />
            </div>
            {(uploadTargetType === 'route' || uploadTargetType === 'stop') && (
              <div>
                <Text type="secondary" style={{ display: 'block', marginBottom: 4 }}>
                  Маршрут
                </Text>
                <Select
                  allowClear
                  placeholder="Маршрут"
                  style={{ width: 160 }}
                  value={uploadRouteId}
                  onChange={(value) => {
                    setUploadRouteId(value);
                    setUploadStopId(undefined);
                  }}
                  options={routes.map((route) => ({ value: route.id, label: `#${route.id}` }))}
                />
              </div>
            )}
            {uploadTargetType === 'stop' && (
              <div>
                <Text type="secondary" style={{ display: 'block', marginBottom: 4 }}>
                  Стоп
                </Text>
                <Select
                  allowClear
                  placeholder="Стоп"
                  style={{ width: 220 }}
                  value={uploadStopId}
                  onChange={setUploadStopId}
                  options={uploadRouteStops.map((stop: { id?: number; address?: string; city?: string; stage?: string }) => ({
                    value: Number(stop.id),
                    label: stop.address || stop.city || `${stop.stage || 'стоп'} #${stop.id}`,
                  }))}
                />
              </div>
            )}
          </Space>
        </Col>
        <Col>
          <Button type="primary" icon={<PlusOutlined />} onClick={openUploadModal}>
            Загрузить документ
          </Button>
        </Col>
      </Row>

      <ClosingPackageBlock
        application={application}
        routes={routes}
        allRows={allRows}
        onPreview={handlePreview}
        onDownload={handleDownload}
        onRefreshDocuments={refreshDocuments}
      />

      <ApplicationDocumentFilters
        filters={filters}
        routes={routes}
        stopOptions={stopOptions}
        onChange={setFilters}
      />

      <Card size="small" title="План документооборота" style={{ marginBottom: 16 }}>
        <Space direction="vertical" style={{ width: '100%' }} size={8}>
          {clientDocumentPlan.map((row) => {
            const status = planStatus(row);
            const canUpload = row.active && !row.uploaded && row.definition.uploadable;
            return (
              <Row key={row.definition.key} align="middle" justify="space-between" gutter={[12, 8]}>
                <Col flex="auto">
                  <Space>
                    <Text strong={row.active}>{row.definition.label}</Text>
                    <Tag color={status.color}>{status.label}</Tag>
                    {!row.active ? <Text type="secondary">Этап еще не наступил</Text> : null}
                  </Space>
                </Col>
                <Col>
                  {canUpload ? (
                    <Button
                      size="small"
                      type="link"
                      icon={<UploadOutlined />}
                      onClick={() => openPlanUpload(row.definition)}
                    >
                      Загрузить
                    </Button>
                  ) : null}
                </Col>
              </Row>
            );
          })}
        </Space>
      </Card>

      {allRows.length > 0 ? (
        <Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>
          Документов: {filteredRows.length} из {allRows.length}
        </Text>
      ) : null}

      {loading ? (
        <div style={{ textAlign: 'center', padding: 48 }}>
          <Spin />
        </div>
      ) : filteredRows.length === 0 ? (
        <Empty description="Документы не найдены" image={Empty.PRESENTED_IMAGE_SIMPLE} style={{ padding: '48px 0' }} />
      ) : (
        <DocumentTable
          variant="application"
          dataSource={filteredRows}
          loading={fetching}
          onPreview={handlePreview}
          onDownload={handleDownload}
          onDelete={handleDelete}
        />
      )}

      <UploadDocumentModal
        visible={uploadModalVisible}
        onCancel={() => {
          setUploadModalVisible(false);
          setUploadPreset(null);
        }}
        onSuccess={handleUploadSuccess}
        application={application}
        route={uploadTargetType === 'application' ? undefined : selectedUploadRoute}
        uploadScope={uploadScope}
        preset={uploadPreset ?? undefined}
      />

      <DocumentPreviewModal open={previewVisible} imageUrl={previewImage} onClose={closePreview} />
    </div>
  );
};

export default ApplicationDocumentsTab;
